<script lang="ts">
  /**
   * ClubTab — ONE honest front door (spec 2026-09-01 §2, item 2a).
   *
   * The page renders by exactly one `ClubFrontDoorState` (clubFrontDoor.ts,
   * pure + tested), resolved from three inputs that already exist:
   *   - getClubStatus (role, iCloud) — the account's answer;
   *   - listCompanions — who is in the club on this phone;
   *   - the shown day's sessions (SharedMorningView, now a headless loader).
   *
   *   noClub             "Your sets, next to theirs." + Start a club (native sheet)
   *   leaderWaiting      Who's in (you) + Invite a clubmate (native sheet)
   *   memberNoSharedSwim "You're in." + Who's in
   *   sharedMorning      the strip + the day roster (a row opens that swim's card)
   *   iCloudBlocked      the native-authored banner, unchanged
   *   statusFailed       the retry card, unchanged
   *
   * Build 13 (B-026, B-027): ONE card (ClubSwimDetail), and nothing else on the tab
   * compares swims. The old "Sets today" compare,
   * its roster-tap picker, the "Who was there" day card, "I was there" and GO DEEPER
   * (lap race, scoreboard, watches disagreed, heart-rate overlay) are UNWIRED here, not
   * deleted: Scott rules on them (docs/build-13.md).
   *
   * Build 14 (B-031): no title tier. The date row is the first thing under the top bar, at the
   * Swim tab's offset; "Club · N swimmers" is an eyebrow under it. The gear lives in the top
   * bar's right slot (App.svelte, topBarSlot) and opens the same native ClubSettingsView, which
   * owns start / invite / remove; Start and Invite below open it too.
   *
   * Build 15 (spec 2026-09-14, Scott "I love 2"): the tab lands on the DAY ROSTER, one row per
   * swim on the date row's day, clubmates first (ClubDayRoster, lib/club/roster.ts). Nothing opens
   * by itself: the build 13 default card is retired. A strip bar SELECTS its day (moves the date
   * row) and never opens a card; a roster row opens that one swim's card with no compare zone,
   * and Close returns to the roster. Reaction pending/failure is per swim (lib/club/reactState.ts).
   */
  import { onMount, tick, untrack } from "svelte";
  import { get } from "svelte/store";
  import ClubSessionHeader from "./ClubSessionHeader.svelte";
  import { openOverlay, refreshOpenClubCalendar } from "../lib/overlay";
  import { clubDayPicked, mergeClubSwims, sessionForDay, sessionForStripTap } from "../lib/clubCalendar";
  import { localDayKey } from "../lib/clubSessions";
  import { selectedSessionId } from "../lib/state";
  import { loadCompanions, classifyCompanion } from "../lib/companions";
  import { bridge, BridgeUnavailableError } from "../bridge/client";
  import type { CompanionSummary, ClubStatusResponse } from "@contract/types";
  import type { SharedMorningSnapshot } from "../lib/sharedMorning";
  import SharedMorningView from "./SharedMorningView.svelte";
  import ClubTeaserCard from "./ClubTeaserCard.svelte";
  import { supportsMethodStore } from "../lib/nativeMethods";
  import {
    clubBooting,
    clubFrontDoor,
    nameSaveOutcome,
    resolveFrontDoorState,
    clubEyebrow,
    inferLeaderName,
    swimCountLine,
    ownHistoryLine,
  } from "../lib/clubFrontDoor";
  import { devClubState, devClubStatus, devCompanions } from "../lib/clubDevState";
  import ClubMembersStrip from "./ClubMembersStrip.svelte";
  import ClubSwimDetail from "./ClubSwimDetail.svelte";
  import ClubDayRoster from "./ClubDayRoster.svelte";
  import type { ClubMember, ClubSnapshot, ClubSwim, PresetEmoji } from "../lib/club/snapshot";
  import { devSnapshotFor, connectClubSnapshot, type SnapshotLoadState } from "../lib/club/snapshotSource";
  import { dayKeyDate, firstWeekday, freshReactions, snapshotNow, weekPages } from "../lib/club/strip";
  import { resolveOpenCard, setsForSwim, withOwnSessionSwims, type OpenCard, type SwimSetLookup } from "../lib/club/swimCard";
  import { createSwimLoader, daySwimRequests } from "../lib/club/swimLoader";
  import { registerVisibleSets, visibleSetsOf } from "../lib/club/visibleSets";
  import { lastSwamLines, noSwimsLine, rosterEyebrow, rosterRows, type RosterRow } from "../lib/club/roster";
  import { reactModel, reactSettled, reactSnapshot, reactTap, type ReactModel } from "../lib/club/reactState";
  import { ownSetTap } from "../lib/club/ownSetTap";
  import { sendReact } from "../lib/club/react";

  type LoadState = "loading" | "ready" | "error";

  // Browser-only `?clubState=…` seam (never on a native shell). When set, it
  // stands in for getClubStatus + listCompanions and picks the fixture variant.
  const devState = devClubState();

  let companionsState = $state<LoadState>("loading");
  let companions = $state<CompanionSummary[]>([]);
  let loadError = $state<string | null>(null);

  // --- Club status (Plan A): account banner + one-time name prompt + native
  // settings entry. Method-gated so a pre-Plan-A shell renders honestly.
  const supportsClubSnapshot = supportsMethodStore("getClubSnapshot");
  let snapshotLoadState = $state<SnapshotLoadState>("loading");
  let retrySnapshot: (() => Promise<void>) | undefined;
  const supportsClubStatus = supportsMethodStore("getClubStatus");
  const supportsClubSettings = supportsMethodStore("openClubSettings");

  let clubStatus = $state<ClubStatusResponse | null>(devState ? devClubStatus(devState) : null);
  let clubStatusLoaded = $state(devState !== null);
  // Codex review finding 1: distinct from "not loaded yet" — true only when a
  // loadClubStatus() call actually threw, so the template can show an honest,
  // retryable line instead of staying silently blank.
  let clubStatusLoadFailed = $state(false);
  let nameDraft = $state("");
  let savingName = $state(false);
  let nameSaveFailed = $state(false);
  let namePromptDismissed = $state(false);

  let frontDoor = $derived(clubFrontDoor(clubStatus));

  async function loadClubStatus(): Promise<void> {
    clubStatusLoadFailed = false;
    try {
      clubStatus = await bridge.getClubStatus();
    } catch {
      // Codex review finding 1: still surfaced (retry card below) — this used to
      // be silent, and left the member with NO visible way to recover.
      clubStatus = null;
      clubStatusLoadFailed = true;
    } finally {
      clubStatusLoaded = true;
    }
  }

  function retryClubStatus(): void {
    clubStatusLoaded = false;
    void loadClubStatus();
  }

  $effect(() => {
    if (!devState && $supportsClubStatus && !clubStatusLoaded) void loadClubStatus();
  });

  async function saveName(): Promise<void> {
    if (savingName) return;
    savingName = true;
    try {
      const res = await bridge.setClubName(nameDraft);
      const outcome = nameSaveOutcome(res);
      nameSaveFailed = outcome.failed;
      if (!outcome.promptShouldStayVisible && clubStatus) {
        clubStatus = { ...clubStatus, myName: res.name ?? nameDraft.trim() };
      }
    } catch {
      nameSaveFailed = nameSaveOutcome({ ok: false }).failed;
    } finally {
      savingName = false;
    }
  }

  /** The gear, "Start a club" and "Invite a clubmate" all open the native
   *  ClubSettingsView — it already carries "Start your club" / "Invite
   *  clubmates" (UICloudSharingController) for the matching role. No
   *  separate share bridge method exists for the web layer to call. */
  function openSettings(): void {
    void bridge.openClubSettings();
  }

  async function refreshCompanions(): Promise<void> {
    companionsState = "loading";
    if (devState) {
      companions = devCompanions(devState);
      companionsState = "ready";
      return;
    }
    try {
      companions = await loadCompanions();
      companionsState = "ready";
    } catch (err) {
      // No native bridge in this render context (dev browser / preview /
      // showcase) — there's no companions API to have called at all, so this
      // isn't a real failure. Render the normal empty state instead of an
      // error message that only ever means something on-device.
      if (err instanceof BridgeUnavailableError && err.reason === "no-handler") {
        companions = [];
        companionsState = "ready";
        return;
      }
      loadError = err instanceof Error ? err.message : String(err);
      companionsState = "error";
    }
  }

  // --- Members strip (spec 2b, C5). On a shell the snapshot arrives as
  // `swimset:club:snapshot` and nothing renders until the first one lands; in
  // the browser the dev state picks a C5 fixture (leaderWaiting = empty).
  let clubSnapshot = $state<ClubSnapshot | null>(devSnapshotFor(devState));
  // Build 15b: an unchanged club is no longer re-applied (snapshotSource guard), so the live clock is also
  // re-read on every club-status refresh, which native sends on each foreground (and each roster bump).
  let clockTick = $state(0);
  let stripNow = $derived.by(() => {
    clockTick;
    return clubSnapshot ? snapshotNow(clubSnapshot, devState === null) : 0;
  });

  // --- The shown day, reported by the headless SharedMorningView (one loader, one truth).
  let snap = $state<SharedMorningSnapshot | null>(null);
  function onSnapshot(s: SharedMorningSnapshot): void {
    snap = s;
  }
  let shownDay = $derived(snap?.shown ? localDayKey(snap.shown.startDate) : null);

  // --- The card. Only a roster row PICKS a card, and it shows only while the date row shows
  // that swim's day (build 15: nothing opens by itself).
  let pickedDay = $state<OpenCard | null>(null);
  // B-028: the strip, the roster and the card read the snapshot PLUS every own swim the session source
  // carries (native cuts own C5 swims to 14 days; the calendar and the date row page months back), so
  // an own day older than 14 days has its bar on the strip's page for that week (VP round 1).
  let cardData = $derived(
    clubSnapshot
      ? withOwnSessionSwims(clubSnapshot, snap?.source ? { viewerId: snap.source.viewerId, swims: snap.source.swims } : null)
      : null,
  );
  let cardSnapshot = $derived(cardData?.snapshot ?? null);
  let openDay = $derived(resolveOpenCard(pickedDay, shownDay));
  const dayOfSwims = (swims: readonly ClubSwim[]): string | null => (swims[0] ? localDayKey(swims[0].date) : null);

  // Build 15: a strip bar SELECTS its day. B-025 (Scott 2026-09-13, "Nav follows the tap"): the date
  // row moves to the bar's day and the roster follows it; no card opens, none toggles.
  function onSelectDay(swims: ClubSwim[]): void {
    const session = sessionForStripTap(snap?.sessions ?? [], swims);
    if (session) selectedSessionId.set(session.sessionId);
  }
  let cardEl = $state<HTMLDivElement | null>(null);
  let rosterEl = $state<HTMLDivElement | null>(null);
  /** A roster row opens exactly that swim's card and brings its top into view. */
  function openRow(row: RosterRow): void {
    pickedDay = { member: row.member, swims: [row.swim] };
    void tick().then(() => cardEl?.scrollIntoView({ block: "start" }));
  }
  /** Close returns to the roster, with the row that was open in view. */
  function closeDay(): void {
    const id = openDay?.swims[0]?.id ?? null;
    pickedDay = null;
    void tick().then(() => rosterEl?.querySelector(`[data-testid="club-roster-row"][data-swim-id="${id}"]`)?.scrollIntoView({ block: "center" }));
  }
  // A fresh snapshot re-resolves the picked card by swim ids, or drops the pick if
  // every swim is gone. Depends on the snapshot only (untrack the pick, or the write
  // below would re-run this effect forever).
  $effect(() => {
    const s = cardSnapshot;
    const cur = untrack(() => pickedDay);
    if (!s || !cur) return;
    const ids = new Set(cur.swims.map((sw) => sw.id));
    const member = s.members.find((m) => m.id === cur.member.id) ?? s.members.find((m) => m.swims.some((sw) => ids.has(sw.id)));
    const swims = member ? member.swims.filter((sw) => ids.has(sw.id)) : [];
    if (member === cur.member && swims.length === cur.swims.length && swims.every((sw, i) => sw === cur.swims[i])) return;
    pickedDay = member && swims.length > 0 ? { member, swims } : null;
  });
  // B-025, the other direction: when the row MOVES to a day the picked card is not on, the pick
  // drops, so going back to that day shows its roster, not a stale card.
  let lastShownDay: string | null = null;
  $effect(() => {
    const day = shownDay;
    const moved = lastShownDay !== null && day !== lastShownDay;
    lastShownDay = day;
    if (!moved) return;
    untrack(() => {
      if (pickedDay && dayOfSwims(pickedDay.swims) !== day) pickedDay = null;
    });
  });

  // Build 16 (parity): on a shell with getClubSwimFirehose, every swim on the shown day is fetched by
  // UUID (3 at a time) and derived like the Sets tab; a changed C5 labelsRevision refetches that swim.
  // `loaderTick` is read only through `clubSwim`, so the rows re-derive when an answer lands and the
  // effect below never re-runs for one.
  const supportsClubSwim = supportsMethodStore("getClubSwimFirehose");
  let loaderTick = $state(0);
  const swimLoader = createSwimLoader((args) => bridge.getClubSwimFirehose(args), () => (loaderTick += 1));
  $effect(() => {
    if (!$supportsClubSwim || !cardSnapshot || !shownDay) return;
    swimLoader.want(daySwimRequests(cardSnapshot, shownDay));
  });

  // Where each swim's sets come from (swimCard.setsForSwim): the loader's answer when it is live,
  // else the firehose when this phone holds it, else the snapshot's C5 sets.
  let cardLookup = $derived<SwimSetLookup | null>(
    snap?.source || $supportsClubSwim
      ? {
          viewerId: snap?.source?.viewerId ?? "",
          firehoseFor: snap?.source?.firehoseFor ?? (() => null),
          labelsFor: snap?.source?.labelsFor,
          sessionOnly: cardData?.sessionOnly,
          loaderActive: $supportsClubSwim,
          clubSwim: (id) => {
            loaderTick;
            return swimLoader.get(id);
          },
        }
      : null,
  );
  function setsFor(member: ClubMember, swim: ClubSwim) {
    return setsForSwim(member, swim, clubSnapshot?.self ?? "", cardLookup);
  }
  // --- The day roster (build 15). The strip's weeks follow the locale's first weekday; a
  // last-swam line links whenever the date row can reach that day (the strip follows to any week),
  // and reads "Tue 8" inside the recent weeks, "Aug 2" before them.
  const stripFirstDay = typeof navigator !== "undefined" ? firstWeekday(navigator.language) : 7;
  let stripDayKeys = $derived(new Set(weekPages(stripNow, stripFirstDay).flatMap((p) => p.days.map((d) => d.key))));
  function sessionForKey(key: string) {
    return sessionForDay(snap?.sessions ?? [], dayKeyDate(key));
  }
  let rows = $derived(cardSnapshot && shownDay ? rosterRows(cardSnapshot, shownDay, setsFor, cardData?.sessionOnly) : []);
  let rosterLines = $derived(
    cardSnapshot && shownDay
      ? lastSwamLines(cardSnapshot, shownDay, { linkable: (key) => sessionForKey(key) !== null, recent: (key) => stripDayKeys.has(key) })
      : [],
  );
  let rosterNoSwims = $derived(cardSnapshot && shownDay ? noSwimsLine(cardSnapshot, shownDay) : null);
  function goToDay(key: string): void {
    const session = sessionForKey(key);
    if (session) selectedSessionId.set(session.sessionId);
  }
  /** Your own open-water swim shows its route where the ribbon would be. Only your own:
   *  a route you do not hold cannot be drawn. */
  function routeFor(swim: ClubSwim): string | null {
    const src = snap?.source;
    if (!src || !clubSnapshot || openDay?.member.id !== clubSnapshot.self) return null;
    const own = src.swims.find((ms) => ms.memberId === src.viewerId && ms.swim.uuid === swim.id);
    return own?.swim.swimmingLocationType === "openWater" ? swim.id : null;
  }

  // --- Reactions (spec 2c, C5). The snapshot's list plus the viewer's own taps
  // applied optimistically; a new snapshot replaces the list (native echoes the
  // tap back). The 24 h filter runs here, on the strip's clock, so the strip,
  // the roster and the card see the same fresh set.
  // Build 15: pending and failure are PER SWIM (reactState); the roster shows every swim's row at
  // once, so one swim's failed tap must never roll back or mark another's.
  // Seeded from the FIRST snapshot on purpose (untrack): later snapshots
  // replace the list in the subscription below, not through this initializer.
  let react = $state<ReactModel>(reactModel(untrack(() => clubSnapshot)?.reactions ?? []));
  let visibleReactions = $derived(freshReactions(react.reactions, stripNow));
  async function onReact(swimId: string, emoji: PresetEmoji | null): Promise<void> {
    if (!clubSnapshot) return;
    const tapped = reactTap(react, swimId, clubSnapshot.self, emoji, new Date(stripNow).toISOString());
    react = tapped.model;
    const res = await sendReact({ swimId, emoji });
    // A failure rolls THAT swim back and says so on its row: an unsent reaction must not sit
    // there looking sent (no silent fallback).
    react = reactSettled(react, swimId, tapped.ticket, res.ok);
    if (!res.ok) console.warn("[club] club:react not confirmed", res.error);
  }

  onMount(() => {
    const onClubStatusRefresh = () => {
      clockTick += 1;
      // Never latch a stale status: reset the loaded flag so the $effect above
      // re-fetches, exactly like a fresh tab re-entry would.
      if (!devState) clubStatusLoaded = false;
      void refreshCompanions();
    };
    window.addEventListener("swimset:club-status-refresh", onClubStatusRefresh);
    const snapshotConnection = connectClubSnapshot((s) => {
      clubSnapshot = s;
      react = reactSnapshot(react, s.reactions);
    }, {
      request: async () => (await bridge.getClubSnapshot()).snapshot,
      onState: (state) => { snapshotLoadState = state; },
    });
    retrySnapshot = snapshotConnection.retry;
    const offCapability = supportsClubSnapshot.subscribe((supported) => {
      if (supported && !devState) void snapshotConnection.retry();
    });
    void refreshCompanions();
    // Build 16 parity proof: the clubmate's side of `window.__swimsetDumpVisibleSets()`.
    const offDump = registerVisibleSets(() => {
      const card = openDay;
      if (card) {
        const swim = card.swims[0];
        return { screen: "club-card", swimId: swim?.id ?? null, sets: swim ? visibleSetsOf(setsFor(card.member, swim).groups) : [] };
      }
      return {
        screen: "club-roster",
        swimId: null,
        sets: [],
        rows: rows.map((r) => ({ swimId: r.swim.id, memberId: r.member.id, source: r.sets.source, sets: visibleSetsOf(r.sets.groups) })),
      };
    });
    return () => {
      window.removeEventListener("swimset:club-status-refresh", onClubStatusRefresh);
      offCapability();
      snapshotConnection.dispose();
      retrySnapshot = undefined;
      offDump();
      swimLoader.dispose();
    };
  });

  let hasSharedSessionForSelectedDay = $derived(
    snap?.shown ? new Set(snap.shown.entries.map((e) => e.memberId)).size >= 2 : false,
  );
  let hasAnySharedSession = $derived(
    snap?.sessions.some((s) => new Set(s.entries.map((e) => e.memberId)).size >= 2) ?? false,
  );
  let viewerSwimCount = $derived(snap?.source ? snap.source.swims.filter((s) => s.memberId === snap!.source!.viewerId).length : 0);
  let ownHistory = $derived(ownHistoryLine(snap?.loading ?? true, snap?.failed ?? false, viewerSwimCount));

  // The role feeding the resolver: the account's answer when we have one;
  // "unknown" while loading / in a plain browser (the data on disk decides).
  let role = $derived<ClubStatusResponse["role"]>(clubStatus?.role ?? "unknown");
  let pageState = $derived(
    resolveFrontDoorState({
      role,
      iCloudOk: clubStatus?.iCloudOk ?? true,
      statusLoadFailed: clubStatusLoadFailed,
      memberCount: companions.length,
      hasSharedSessionForSelectedDay,
      hasAnySharedSession,
    }),
  );
  // One roster for the headcount (VP round 1, fix 3): when the C5 snapshot is
  // here it is the club's member list; `companions` (legacy bridge) only until then.
  let clubmateCount = $derived(
    clubSnapshot ? clubSnapshot.members.filter((m) => m.id !== clubSnapshot!.self).length : companions.length,
  );
  let eyebrow = $derived(clubEyebrow(pageState, clubmateCount));
  // B-033: once the date row is pinned, a cover band hangs under its edge so nothing scrolling
  // under it (the strip's reaction badges were the case) floats alone below the pill. A 1 px
  // sentinel at the top of the page says when the row has stuck; at rest there is no band, so the
  // eyebrow under the row is never covered.
  let sentinelEl = $state<HTMLDivElement | null>(null);
  let dateRowStuck = $state(false);
  $effect(() => {
    const el = sentinelEl;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => {
      dateRowStuck = !e.isIntersecting && e.boundingClientRect.top < (e.rootBounds?.top ?? 0);
    });
    io.observe(el);
    return () => io.disconnect();
  });
  // B-031: the date row reports whether it shows its short label; the card's date line follows.
  let dateRowShort = $state(false);
  function onDateFit(abbreviated: boolean): void {
    dateRowShort = abbreviated;
  }
  // Don't flash "Start a club" at a leader while the answers are still in
  // flight: hold the body until status (on a shell), companions and the
  // session source have all settled.
  // Build 15b: only the FIRST load holds the body. Every `swimset:club-status-refresh` re-fetches
  // companions and status; gating on those re-fetches unmounted the strip and roster each time.
  let snapshotRequired = $derived(pageState === "sharedMorning" || pageState === "memberNoSharedSwim" || pageState === "leaderWaiting");
  let snapshotUnavailable = $derived(snapshotRequired && clubSnapshot === null && snapshotLoadState === "error");
  let clubShown = $state(false);
  let booting = $derived(
    clubBooting({
      shown: clubShown,
      companionsLoading: companionsState === "loading",
      statusPending: !devState && $supportsClubStatus && !clubStatusLoaded,
      sessionsLoading: clubSnapshot === null && (snap?.loading ?? true) && !snapshotUnavailable,
      snapshotPending: snapshotRequired && clubSnapshot === null && !snapshotUnavailable,
    }),
  );
  $effect(() => {
    if (!booting) clubShown = true;
  });
  // --- The date row (lane swim-club-calendar): the Swim tab's ‹ date › row, on
  // top of the Club tab. Its calendar button opens the same sheet, Club variant
  // (a swimmer count per day), fed by the SAME merged swims the sessions group:
  // the session source plus every clubmate swim the club snapshot carries (B-016:
  // full history; only the strip keeps 14 days).
  let showDateRow = $derived(
    !booting && (snap?.sessions.length ?? 0) > 0 && pageState !== "noClub" && pageState !== "iCloudBlocked",
  );
  let clubCalendarSwims = $derived(snap?.source ? mergeClubSwims(snap.source.swims, snap.source.viewerId, clubSnapshot) : null);
  function openClubCalendar(): void {
    if (!clubCalendarSwims) return;
    openOverlay({ kind: "your-swims", club: clubCalendarSwims });
  }
  // B-020: a snapshot (or a source reload) that lands while the calendar is open
  // re-derives its counts; a sheet opened before the snapshot used to stay stale.
  $effect(() => {
    if (clubCalendarSwims) refreshOpenClubCalendar(clubCalendarSwims);
  });
  // A day picked in the calendar drops any picked card, so that day's roster shows, and
  // brings it up under the sticky date row. The ‹ › arrows never scroll.
  let seenDayPick = get(clubDayPicked);
  $effect(() => {
    const n = $clubDayPicked;
    if (n === seenDayPick) return;
    seenDayPick = n;
    untrack(() => {
      pickedDay = null;
    });
    void tick().then(() => rosterEl?.scrollIntoView({ block: "start" }));
  });
  const fixtureVariant = devState && devState !== "noClub" ? devState : "default";
  // The gear / Start / Invite buttons need the native sheet. In the browser dev
  // seam they render (the stub bridge no-ops) so the screens match the mockups.
  let canOpenSettings = $derived((frontDoor.showSettingsButton && $supportsClubSettings) || devState !== null);
  // The strip opens the tab whenever there is a club to show. noClub has no
  // members to strip; the iCloud banner and the retry card keep the page.
  let showStrip = $derived(
    !booting && clubSnapshot !== null && pageState !== "noClub" && pageState !== "iCloudBlocked",
  );
  let myName = $derived(clubStatus?.myName && clubStatus.myName !== "" ? clubStatus.myName : "You");
  let leaderName = $derived(inferLeaderName(role, companions));
  let isLeader = $derived(role === "leader");
</script>

<!-- A div, not a section: app.css frames every `#app-root section[class]` as a glass card, and this page IS the cards. -->
<div class="club">
  <div class="stick-sentinel" bind:this={sentinelEl} aria-hidden="true"></div>
  {#if showDateRow && snap}
    <div class="date-row" class:stuck={dateRowStuck} data-testid="club-date-row">
      <ClubSessionHeader sessions={snap.sessions} onOpenCalendar={openClubCalendar} onFit={onDateFit} />
    </div>
  {/if}

  <!-- B-031: the retired title tier, as the app's eyebrow, UNDER the row so the row sits where Swim's does. -->
  <p class="eyebrow" data-testid="club-eyebrow">{eyebrow}</p>

  {#if frontDoor.banner}
    <div class="card frontdoor-banner" role="status">
      <p class="cta">The club needs iCloud</p>
      <p class="muted">{frontDoor.banner}</p>
    </div>
  {/if}

  {#if clubStatusLoadFailed}
    <!-- Codex review finding 1: an honest, retryable line for a bridge/network hiccup —
         distinct from frontDoor.banner, which is reserved for the native-authored iCloud
         account message. -->
    <div class="card frontdoor-banner" role="status">
      <p class="cta">Couldn't check your club status</p>
      <p class="muted">Check your connection, or open Club settings with the gear above.</p>
      <button class="btn-quiet" type="button" onclick={retryClubStatus}>Try again</button>
    </div>
  {/if}

  {#if frontDoor.shouldPromptForName && !namePromptDismissed}
    <div class="card name-card">
      <p class="cta">What's your name?</p>
      <p class="muted">Your club sees this name next to your swims.</p>
      <input
        class="name-input"
        type="text"
        maxlength="40"
        placeholder="Your name"
        bind:value={nameDraft}
        aria-label="Your name in the club"
      />
      {#if nameSaveFailed}
        <p class="muted name-fail">Couldn't save your name — check your connection and try again.</p>
      {/if}
      <div class="name-actions">
        <button class="btn-quiet" type="button" onclick={() => (namePromptDismissed = true)}>Later</button>
        <button
          class="btn-primary"
          type="button"
          disabled={savingName || nameDraft.trim().length === 0}
          onclick={() => void saveName()}
        >
          {savingName ? "Saving…" : "Save"}
        </button>
      </div>
      <p class="muted lock-line">🔒 Only your club sees your swims. Nothing is public.</p>
    </div>
  {/if}

  {#if companionsState === "error"}
    <p class="muted">Couldn't load your club: {loadError}</p>
  {/if}

  {#if booting && pageState !== "iCloudBlocked" && pageState !== "statusFailed"}
    <p class="muted center">Loading your club…</p>
  {/if}

  {#if snapshotUnavailable}
    <div class="card frontdoor-banner" role="status" data-testid="club-snapshot-recovery">
      <p class="cta">Couldn't load your club's swims</p>
      {#if $supportsClubSnapshot}
        <p class="muted">Try loading them again.</p>
        <button class="btn-quiet" type="button" onclick={() => void retrySnapshot?.()}>Try again</button>
      {:else}
        <p class="muted">Close and reopen Swim Set, then return to Club.</p>
      {/if}
    </div>
  {/if}

  {#if !booting && !snapshotUnavailable && snap?.failed}
    <!-- The loader failed wholesale: say so, never an empty strip that reads as "nobody swam". -->
    <p class="muted center" role="status">Couldn't load your club's swims. Leave the Club tab and come back to retry.</p>
  {/if}

  {#if showStrip && clubSnapshot}
    <ClubMembersStrip
      snapshot={cardSnapshot ?? clubSnapshot}
      now={stripNow}
      firstDay={stripFirstDay}
      selectedDay={shownDay}
      {onSelectDay}
      reactions={visibleReactions}
    />
    {#if openDay}
      <div class="card-slot" bind:this={cardEl}>
        <ClubSwimDetail
          member={openDay.member}
          swims={openDay.swims}
          isSelf={openDay.member.id === clubSnapshot.self}
          selfId={clubSnapshot.self}
          now={stripNow}
          {setsFor}
          compare={null}
          sessionOnly={cardData?.sessionOnly}
          {routeFor}
          reactions={visibleReactions}
          onReact={(swimId, emoji) => void onReact(swimId, emoji)}
          reactFailed={react.status[openDay.swims[0]?.id ?? ""]?.failed ?? false}
          shortDate={dateRowShort}
          onClose={closeDay}
        />
      </div>
    {:else if shownDay && rows.length > 0}
      <div class="card-slot" bind:this={rosterEl}>
        <ClubDayRoster
          eyebrow={rosterEyebrow(shownDay, rows.length)}
          {rows}
          lines={rosterLines}
          noSwims={rosterNoSwims}
          selfId={clubSnapshot.self}
          reactions={visibleReactions}
          reactStatus={react.status}
          onOpen={openRow}
          onReviewStrokes={(row) => ownSetTap(row.isSelf, row.swim)}
          onReact={(swimId, emoji) => void onReact(swimId, emoji)}
          onGoToDay={goToDay}
        />
      </div>
    {/if}
  {/if}

  {#if !booting && !snapshotUnavailable && pageState === "noClub"}
    <div class="card">
      <p class="big">Your sets, next to theirs.</p>
      <p class="body">Who was faster on each set, and on each stroke. Every length, side by side.</p>
      {#if canOpenSettings}
        <button class="btn" type="button" onclick={openSettings}>Start a club</button>
      {:else}
        <!-- Plain browser / pre-club shell: there is no sheet to open. Say so, never a dead tap. -->
        <p class="hint">Start a club from Swim Set Club on your iPhone.</p>
      {/if}
      <p class="hint">You'll be the leader. You invite people by sending a private link in Messages.</p>
    </div>
    <div class="card">
      <p class="big small">Got an invite?</p>
      <p class="hint">Tap the link in Messages. You'll land right here, in your crew's club.</p>
    </div>
    <p class="label sec-gap">What a shared set looks like</p>
    <ClubTeaserCard />
  {/if}

  {#if !booting && !snapshotUnavailable && pageState === "leaderWaiting"}
    <div class="card">
      <p class="label">Who's in</p>
      <div class="roster-row">
        <span class="who">{myName}<small>you · leader</small></span>
        <span class="meta">{ownHistory.line}{#if ownHistory.complete}<small class="scope">all time</small>{/if}</span>
      </div>
      {#if canOpenSettings}
        <button class="btn" type="button" onclick={openSettings}>Invite a clubmate</button>
      {/if}
      <p class="hint">Sends a private link in Messages. Only people you invite can see your swims.</p>
    </div>
  {/if}

  {#if !booting && !snapshotUnavailable && pageState === "memberNoSharedSwim"}
    <div class="card">
      <p class="big">{isLeader ? "Your club is set." : "You're in."}</p>
      <p class="body">
        Next time you and {leaderName ?? (isLeader ? "a clubmate" : "your clubmates")} swim the same morning, your sets line up here, stroke by stroke.
      </p>
    </div>
    <div class="card">
      <p class="label">Who's in</p>
      {#if isLeader}
        <div class="roster-row">
          <span class="who">{myName}<small>you · leader</small></span>
          <span class="meta">{ownHistory.line}{#if ownHistory.complete}<small class="scope">all time</small>{/if}</span>
        </div>
      {/if}
      {#each companions as c (c.originId)}
        <div class="roster-row">
          <span class="who">{c.swimmerLabel}{#if c.swimmerLabel === leaderName}<small>leader</small>{/if}</span>
          <span class="meta">{swimCountLine(c.workoutCount)}<small class="scope">all time</small></span>
        </div>
        {#if classifyCompanion(c) === "unsupported-schema"}
          <p class="reject-line">Their swims were recorded by a newer version of Swim Set Club than this build. Update in TestFlight to see them.</p>
        {/if}
      {/each}
      {#if !isLeader}
        <div class="roster-row">
          <span class="who">{myName}<small>you</small></span>
          <span class="meta">{ownHistory.line}{#if ownHistory.complete}<small class="scope">all time</small>{/if}</span>
        </div>
      {/if}
    </div>
  {/if}

  <!-- The session + firehose loader, no markup of its own (B-027). -->
  <SharedMorningView headless={true} {clubSnapshot} {fixtureVariant} {onSnapshot} />
</div>

<style>
  .club {
    /* main already pads 16px; the mockups run cards edge to that padding. */
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  /* The app's eyebrow (app.css .t-caption, the strip's week label). */
  .eyebrow {
    font: var(--type-caption);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: 0;
  }

  .card {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: 14px var(--space-4);
    text-align: left;
  }
  .card-slot {
    /* A calendar pick or a roster row brings the roster or the card up under the sticky date row. */
    scroll-margin-top: calc(env(safe-area-inset-top, 0px) + 64px);
  }
  /* The date row stays in reach while the strip and the card scroll under it. */
  .date-row {
    position: sticky;
    top: env(safe-area-inset-top, 0px);
    z-index: 5;
    border-radius: var(--radius-card);
    /* The app's glass, over a mostly opaque canvas tint: strip labels scrolling
       under the row blur away instead of showing through (VP round 1 polish). */
    background: color-mix(in srgb, var(--bg-canvas) 72%, transparent);
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
  }
  /* B-033: absolutely placed, so it takes no flex gap; it leaves the viewport as the row sticks. */
  .club {
    position: relative;
  }
  .stick-sentinel {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }
  /* B-033 (VP round 2): once pinned, the row is opaque canvas, and so are the first 20 px under it,
     then a 10 px fade. At 72% / 92% the day letters and the badges still read through. The strip's
     badge line is 14 px, 3 px under the day letters, so 20 px hides it by the time the letters are gone. */
  .date-row.stuck {
    background: var(--bg-canvas);
  }
  .date-row.stuck::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 30px;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      var(--bg-canvas) 0,
      var(--bg-canvas) 20px,
      color-mix(in srgb, var(--bg-canvas) 0%, transparent) 30px
    );
  }
  .muted.center {
    text-align: center;
    padding: var(--space-4) 0;
  }
  .label {
    font: var(--type-caption);
    letter-spacing: 0.05em;
    color: var(--text-muted);
    text-transform: uppercase;
    margin: 0 0 var(--space-2);
  }
  .sec-gap {
    margin-top: var(--space-2);
    margin-bottom: 0;
  }
  .big {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
    margin: 0;
  }
  .big.small {
    font-size: 17px;
  }
  .body {
    font-size: 15px;
    line-height: 1.45;
    color: var(--text-body);
    margin: var(--space-2) 0 0;
  }
  .hint {
    font-size: 14px;
    line-height: 1.45;
    color: var(--text-muted);
    margin: 10px 0 0;
  }
  .btn {
    display: block;
    width: 100%;
    min-height: 48px;
    text-align: center;
    background: var(--accent-primary);
    color: var(--text-on-accent);
    border: none;
    border-radius: var(--radius-button);
    font-family: var(--font-text);
    font-weight: 600;
    font-size: 17px;
    padding: 14px var(--space-4);
    margin-top: var(--space-3);
    cursor: pointer;
  }

  /* Who's in roster (leaderWaiting / memberNoSharedSwim). */
  .roster-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    padding: 9px 0;
    border-bottom: 1px solid var(--divider);
  }
  .roster-row:last-of-type {
    border-bottom: none;
  }
  .roster-row .who {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 17px;
  }
  .roster-row .who small {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    margin-left: 6px;
    text-transform: uppercase;
  }
  .roster-row .meta {
    color: var(--text-muted);
    font-size: 14px;
    text-align: right;
  }
  /* The roster counts are lifetime; the strip's are the past 7 days. Say so
     (VP round 1, fix 5). */
  .roster-row .meta .scope {
    display: block;
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .reject-line {
    font-size: 13px;
    color: var(--accent-fire);
    margin: 0 0 var(--space-2);
  }
  .cta {
    font: var(--type-body);
    color: var(--text-primary);
    margin: 0 0 var(--space-2);
  }
  .muted {
    font: var(--type-body-sm);
    color: var(--text-muted);
    margin: 0;
  }

  /* --- Club status cards (Plan A) --- */
  .frontdoor-banner {
    text-align: center;
  }
  .name-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .name-input {
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font-size: 16px;
    padding: 12px 14px;
  }
  .name-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
  .btn-primary {
    background: var(--accent-primary);
    border: none;
    border-radius: var(--radius-button);
    color: var(--text-on-accent);
    font-size: 15px;
    font-weight: 600;
    padding: 10px 18px;
  }
  .btn-primary:disabled {
    opacity: 0.45;
  }
  .btn-quiet {
    background: transparent;
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-body);
    font-size: 15px;
    padding: 10px 14px;
    margin-top: var(--space-2);
  }
  .name-fail {
    color: var(--accent-error);
  }
  .lock-line {
    font-size: 13px;
  }
</style>
